// src/firebase.ts
import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, connectAuthEmulator } from 'firebase/auth';
import { initializeAppCheck, ReCaptchaV3Provider, AppCheck } from 'firebase/app-check';
import { Firestore, getFirestore, initializeFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, FirebaseStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator, Functions } from 'firebase/functions';
import { QA_FIREBASE_CONFIG } from '@/config/firebase-qa';

const must = (v: string | undefined, name: string): string => {
  if (!v || v === 'undefined') {
    throw new Error(`Missing ${name}. Check your .env.local`);
  }
  return v;
};

// 🎯 P4: Get environment variable from Cypress-injected config or Vite
// Cypress injects window.__VITE_ENV__ before app loads to ensure emulator config is available
function getViteEnv(key: string): string | undefined {
  if (typeof window !== 'undefined' && (window as any).__VITE_ENV__?.[key]) {
    return (window as any).__VITE_ENV__[key];
  }
  return import.meta.env[key];
}

// Check if QA mode is enabled (from environment variable set at build time)
const isQAMode = getViteEnv('VITE_ENV') === 'qa';

// In Cypress E2E runs, allow initializing Firebase with harmless placeholder
// values only when real environment configuration is unavailable.
const isE2E = typeof window !== 'undefined' && typeof (window as unknown as { Cypress?: unknown }).Cypress !== 'undefined';

const envConfig = {
  apiKey: getViteEnv('VITE_FIREBASE_API_KEY'),
  authDomain: getViteEnv('VITE_FIREBASE_AUTH_DOMAIN'),
  projectId: getViteEnv('VITE_FIREBASE_PROJECT_ID'),
  storageBucket: getViteEnv('VITE_FIREBASE_STORAGE_BUCKET'),
  appId: getViteEnv('VITE_FIREBASE_APP_ID'),
  messagingSenderId: getViteEnv('VITE_FIREBASE_MESSAGING_SENDER_ID'),
} as const;

const hasEnvConfig = Object.values(envConfig).every((value) => typeof value === 'string' && value.length > 0);

console.log('[Firebase] Environment check:', {
  isQAMode,
  isE2E,
  hasEnvConfig,
  VITE_USE_EMULATORS: getViteEnv('VITE_USE_EMULATORS'),
  projectId: envConfig.projectId,
});

// Use QA config if QA mode is enabled, otherwise use production config
const config = isQAMode 
  ? QA_FIREBASE_CONFIG
  : isE2E && !hasEnvConfig
  ? {
      apiKey: 'e2e-api-key',
      authDomain: 'localhost',
      projectId: 'demo-e2e',
      storageBucket: 'demo-e2e.appspot.com',
      appId: 'e2e-app-id',
      messagingSenderId: 'e2e',
    }
  : {
      apiKey: must(envConfig.apiKey, 'VITE_FIREBASE_API_KEY'),
      authDomain: must(envConfig.authDomain, 'VITE_FIREBASE_AUTH_DOMAIN'),
      projectId: must(envConfig.projectId, 'VITE_FIREBASE_PROJECT_ID'),
      storageBucket: must(envConfig.storageBucket, 'VITE_FIREBASE_STORAGE_BUCKET'),
      appId: must(envConfig.appId, 'VITE_FIREBASE_APP_ID'),
      messagingSenderId: must(envConfig.messagingSenderId, 'VITE_FIREBASE_MESSAGING_SENDER_ID'),
    };

// PROD DIAGNOSTIC: Log Firebase config (non-sensitive) - P0 incident 2025-12-12
// ALWAYS log in production to diagnose projectId/authDomain mismatches
if (typeof window !== 'undefined') {
  console.info(
    `[ThreadLock] Firebase config: projectId=${config.projectId}, authDomain=${config.authDomain}, appId=${config.appId}${
      isQAMode ? ' (QA MODE)' : ''
    }`
  );
}

// PROD FAILSAFE: Validate critical Firebase config at runtime - P0 incident 2025-12-12
if (!config.projectId || !config.authDomain || !config.appId) {
  const errorMsg = `[ThreadLock] FATAL: Firebase config incomplete. projectId=${config.projectId}, authDomain=${config.authDomain}, appId=${config.appId}`;
  console.error(errorMsg);
  // Throw error to prevent silent Firestore 400s
  throw new Error('Firebase configuration is incomplete. Cannot initialize app.');
}

const app: FirebaseApp = getApps().length ? getApp() : initializeApp(config);

export const auth: Auth = getAuth(app);

// [P3 FIX] Expose E2E test hook for app-side authentication
// This eliminates cross-SDK persistence issues by using the app's own Auth instance
if (typeof window !== 'undefined' && isE2E) {
  Promise.all([
    import('firebase/auth'),
    import('firebase/firestore')
  ]).then(([
    { signInWithEmailAndPassword, createUserWithEmailAndPassword, browserLocalPersistence, setPersistence },
    { collection, getDocs, doc, updateDoc, getDoc }
  ]) => {
    (window as any).__TL_E2E__ = {
      login: async (email: string, password: string) => {
        await setPersistence(auth, browserLocalPersistence);
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        console.log('[E2E login] Signed in:', userCredential.user.uid, email);
        return userCredential.user;
      },
      
      // [P2 Admin Support] Deterministic claim polling - NO BLIND WAITS
      waitForClaims: async (expectedClaims: { admin?: boolean; role?: string }, timeoutMs = 20000) => {
        const startTime = Date.now();
        const pollInterval = 500;
        // eslint-disable-next-line no-restricted-syntax -- E2E test helper, not a component
        const user = auth.currentUser;
        
        if (!user) {
          throw new Error('[E2E waitForClaims] No user logged in');
        }

        console.log('[E2E waitForClaims] Starting poll for claims:', expectedClaims);
        console.log('[E2E waitForClaims] UID:', user.uid);
        console.log('[E2E waitForClaims] Email:', user.email);
        console.log('[E2E waitForClaims] Project:', app.options.projectId);
        
        while (Date.now() - startTime < timeoutMs) {
          // Force token refresh
          await user.getIdToken(true);
          const tokenResult = await user.getIdTokenResult();
          const claims = tokenResult.claims;
          
          // Check if expected claims match
          let claimsMatch = true;
          if (expectedClaims.admin !== undefined && claims.admin !== expectedClaims.admin) {
            claimsMatch = false;
          }
          if (expectedClaims.role !== undefined && claims.role !== expectedClaims.role) {
            claimsMatch = false;
          }
          
          if (claimsMatch) {
            console.log('[E2E waitForClaims] ✓ Claims verified after', Date.now() - startTime, 'ms');
            console.log('[E2E waitForClaims] Claims:', { admin: claims.admin, role: claims.role });
            return claims;
          }
          
          // Log current state every 2 seconds
          if ((Date.now() - startTime) % 2000 < pollInterval) {
            console.log('[E2E waitForClaims] Still polling... Current claims:', { admin: claims.admin, role: claims.role });
          }
          
          await new Promise(resolve => setTimeout(resolve, pollInterval));
        }
        
        // Timeout - provide diagnostic info
        const finalTokenResult = await user.getIdTokenResult();
        const finalClaims = finalTokenResult.claims;
        
        const diagnostics = {
          uid: user.uid,
          email: user.email,
          projectId: app.options.projectId,
          expectedClaims,
          actualClaims: { admin: finalClaims.admin, role: finalClaims.role },
          allClaims: finalClaims,
          timeoutMs,
        };
        
        console.error('[E2E waitForClaims] TIMEOUT - Claims never appeared');
        console.error('[E2E waitForClaims] Diagnostics:', JSON.stringify(diagnostics, null, 2));
        
        throw new Error(`[E2E waitForClaims] Timeout after ${timeoutMs}ms. Expected: ${JSON.stringify(expectedClaims)}, Got: ${JSON.stringify({ admin: finalClaims.admin, role: finalClaims.role })}`);
      },
      
      // [P2 Admin Support] Firestore operations using already-initialized db
      firestore: {
        getTickets: async () => {
          const ticketsRef = collection(db, 'supportTickets');
          return await getDocs(ticketsRef);
        },
        updateTicket: async (ticketId: string, data: Record<string, any>) => {
          const ticketRef = doc(db, 'supportTickets', ticketId);
          // Ensure data is a plain object by spreading
          return await updateDoc(ticketRef, { ...data });
        },
        getTicket: async (ticketId: string) => {
          const ticketRef = doc(db, 'supportTickets', ticketId);
          return await getDoc(ticketRef);
        },
      },
    };
  });
}

// Connect to Firebase emulators in dev/test mode (must be done before any Firebase operations)
// 🎯 DETERMINISTIC: Use explicit env var, not heuristics
// 🔒 PRODUCTION SAFETY: NEVER allow emulators in production builds
const forceEmulators = getViteEnv('VITE_USE_EMULATORS') === 'true';
const isCypress = typeof window !== 'undefined' && !!(window as any).Cypress;

// 🚨 FATAL INVARIANT: Prevent emulator connection in production builds
if (import.meta.env.PROD) {
  const projectId = getViteEnv('VITE_FIREBASE_PROJECT_ID');
  
  // Check 1: Emulator flag explicitly set in production
  if (forceEmulators) {
    throw new Error(
      `[PROD SAFETY] VITE_USE_EMULATORS=true in production build. ` +
      `This would connect production app to localhost emulator. ` +
      `Remove VITE_USE_EMULATORS from .env.local or use mode-specific env files.`
    );
  }
  
  // Check 2: Suspicious project IDs in production
  if (projectId && (
    projectId.includes('local-') ||
    projectId.includes('-qa') ||
    projectId.includes('test-') ||
    projectId.includes('emulator')
  )) {
    console.warn(
      `[PROD SAFETY] Suspicious project ID in production: ${projectId}. ` +
      `Verify this is correct for production deployment.`
    );
  }
}

// ⚠️ SECURITY: Only expose auth to window in Cypress + emulator mode
// This prevents accidental exposure in production builds
if (typeof window !== 'undefined' && isCypress && forceEmulators) {
  (window as any).__TL_AUTH__ = auth;
  (window as any).__FIREBASE_AUTH__ = auth;
  console.warn('[CYPRESS] Auth exposed to window (emulator mode only)');
}

// Initialize Firestore with long-polling for Cypress/emulator stability
// In Cypress: Force long-polling to avoid Listen channel issues in browser automation
// In production: Use default (streaming) for optimal performance

// CRITICAL: Must call initializeFirestore BEFORE any getFirestore call
// Cannot switch after Firestore is already initialized
let db: Firestore;
try {
  if (isCypress) {
    db = initializeFirestore(app, {
      experimentalForceLongPolling: true,
      // @ts-expect-error - useFetchStreams not in types but works
      useFetchStreams: false,
    });
    console.info('[CYPRESS] Firestore initialized with long-polling');
  } else {
    db = getFirestore(app);
  }
} catch (error) {
  console.error('[Firebase] Firestore initialization error:', error);
  // Fallback to getFirestore if initializeFirestore fails
  db = getFirestore(app);
}

export { db };
export const storage: FirebaseStorage = getStorage(app);
export const functions: Functions = getFunctions(app, 'us-central1');

// ⚠️ CYPRESS FAIL-CLOSED: If Cypress detected WITHOUT forced emulators, throw immediately
// EXCEPT for Lane B (E2E_TARGET=qa) which tests real QA deployment
const isLaneB = typeof window !== 'undefined' && 
                (window as { Cypress?: { env?: (key: string) => string } }).Cypress?.env?.('E2E_TARGET') === 'qa';
if (isCypress && !forceEmulators && !isLaneB) {
  const msg = 'CYPRESS_MODE_REQUIRES_EMULATORS: Set VITE_USE_EMULATORS=true in .env.cypress (or E2E_TARGET=qa for Lane B testing)';
  console.error(msg);
  throw new Error(msg);
}

if (isLaneB) {
  console.info('🎯 [LANE B] Cypress detected with E2E_TARGET=qa - testing real QA deployment (no emulators)');
}

// 🔒 HARD GUARD: Only use emulators in DEV mode AND with explicit flag
const useEmulators = import.meta.env.DEV && forceEmulators;

if (useEmulators) {
  try {
    // Auth emulator - use environment variable if available, otherwise default
    const authEmulatorEnv = getViteEnv('VITE_FIREBASE_AUTH_EMULATOR_HOST') || 
                           getViteEnv('VITE_AUTH_EMULATOR_HOST') ||
                           (typeof process !== 'undefined' && process.env?.FIREBASE_AUTH_EMULATOR_HOST);
    const authEmulatorHost = authEmulatorEnv 
      ? (authEmulatorEnv.startsWith('http') ? authEmulatorEnv : `http://${authEmulatorEnv}`)
      : 'http://127.0.0.1:9099';
     
    if (!(auth as { _deleted?: boolean })._deleted && !(auth as { emulatorConfig?: unknown }).emulatorConfig) {
      try {
        connectAuthEmulator(auth, authEmulatorHost, { disableWarnings: true });
        
        // Set global flag for environment detection (used by subscription guards)
        if (typeof window !== 'undefined') {
          (window as any).__FIREBASE_EMULATOR_HOST__ = authEmulatorHost;
        }
        
        // A1: Unmistakable emulator connection log
        console.info('[AUTH] emulator connected:', authEmulatorHost);
        
        if (import.meta.env.DEV) {
          console.info('[Firebase] Connected to Auth emulator at', authEmulatorHost);
        }
      } catch (authEmulatorError) {
        // Don't crash the app if emulator connection fails - log and continue
        console.warn('[Firebase] Auth emulator connection failed (continuing without emulator):', authEmulatorError);
        console.warn('[Firebase] If using emulator, verify it is running at', authEmulatorHost);
      }
    }
  } catch (err) {
    const error = err as Error;
    if (!error?.message?.includes('already')) {
      console.warn('[Firebase] Failed to connect Auth emulator:', err);
    }
  }

  try {
    connectFirestoreEmulator(db, '127.0.0.1', 8080);
    console.info('[Firebase] Connected to Firestore emulator at 127.0.0.1:8080');
  } catch (err) {
    const msg = (err as Error)?.message ?? String(err);
    // Firestore throws if already connected or settings are locked — both are acceptable here
    console.warn('[Firebase] Firestore emulator connect skipped:', msg);
  }

  // Deterministic emulator mode log
  console.info('[Firebase] Emulator mode:', {
    useEmulators,
    forceEmulators,
    viteDEV: import.meta.env.DEV,
    projectId: app.options.projectId,
  });
  
  try {
    // Functions emulator
    connectFunctionsEmulator(functions, '127.0.0.1', 5001);
    if (import.meta.env.DEV) {
      console.info('[Firebase] Connected to Functions emulator at 127.0.0.1:5001');
    }
  } catch (err) {
    const error = err as Error;
    if (!error?.message?.includes('already')) {
      console.warn('[Firebase] Failed to connect Functions emulator:', err);
    }
  }
  
  try {
    // Storage emulator
    connectStorageEmulator(storage, '127.0.0.1', 9199);
    if (import.meta.env.DEV) {
      console.info('[Firebase] Connected to Storage emulator at 127.0.0.1:9199');
    }
  } catch (err) {
    const error = err as Error;
    if (!error?.message?.includes('already')) {
      console.warn('[Firebase] Failed to connect Storage emulator:', err);
    }
  }
} else {
  console.info('[AUTH] emulator NOT connected (production mode)');
}

// App Check (client)
const appCheckEnabled = import.meta.env.VITE_ENABLE_APP_CHECK === 'true';
const siteKey = !isE2E ? import.meta.env.VITE_RECAPTCHA_SITE_KEY : undefined;
let appCheck: AppCheck | undefined;
if (appCheckEnabled && siteKey) {
  // Use a debug token locally. In Vite, put VITE_APPCHECK_DEBUG_TOKEN=auto in .env.local
  if (import.meta.env.VITE_APPCHECK_DEBUG_TOKEN) {
    (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string }).FIREBASE_APPCHECK_DEBUG_TOKEN =
      import.meta.env.VITE_APPCHECK_DEBUG_TOKEN === 'auto'
        ? true
        : import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;
  } else if (import.meta.env.DEV) {
    (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }

  appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(siteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

// Export appCheck at the top level
export { appCheck, app };


